import * as THREE from 'three';
import { AIRFIELD } from '../core/SimConfig.js';
import { DEG2RAD } from '../utils.js';

const DETAIL_LOD_DISTANCE_SQ = 2000 * 2000;

/**
 * Static airfield placed at a fixed world location and snapped to the terrain
 * surface. Owns its scene-graph root and holds the underlying GLB scene so
 * later phases can drive per-piece LOD visibility, runway shader uniforms,
 * and door / windsock / windsock animations from this class.
 *
 * Position is re-read from `SimConfig.AIRFIELD` every frame, which makes
 * fine-tuning the placement a one-line config edit (and supports HMR).
 * Surface snap is deferred — the heightmap for the airfield tile streams in
 * asynchronously, so the model stays hidden until the first valid query.
 */
export default class Airfield {
  constructor({ model, scene, terrainCollider, asphaltTexture = null }) {
    this.scene = scene;
    this.terrainCollider = terrainCollider;
    this.model = model;

    this.group = new THREE.Group();
    this.group.name = 'Airfield';
    this.group.add(model);

    this._detailMeshes = [];
    model.traverse((child) => {
      if (!child.isMesh) return;
      child.castShadow = true;
      child.receiveShadow = true;
      if (child.name.startsWith('_details')) this._detailMeshes.push(child);
    });

    // ── Runway detail shader ─────────────────────────────────────────────
    // Find the material named 'runway' and patch its standard shader so
    // its diffuse is multiplied by a tiled asphalt sample, and reuse the
    // same texture as a bumpMap for fine-grain surface detail.
    this._runwayMat = null;
    this._runwayUniforms = null;
    if (asphaltTexture) this._setupRunwayDetail(asphaltTexture);

    this.group.visible = false;   // shown once snapped to the surface
    this._placed = false;

    scene.add(this.group);
  }

  _setupRunwayDetail(asphaltTexture) {
    // Locate the 'runway' material on any mesh in the model.
    let runwayMat = null;
    this.model.traverse((child) => {
      if (runwayMat || !child.isMesh) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const m of mats) {
        if (m && m.name === 'runway') { runwayMat = m; break; }
      }
    });
    if (!runwayMat) return;

    const cfg = AIRFIELD.runway;

    // Three's standard bumpMap pipeline handles UV scaling via the texture's
    // own repeat — set it on the asphalt texture so Three's vBumpMapUv is
    // already scaled when read by the built-in bump fragment.
    asphaltTexture.repeat.set(cfg.uvScale, cfg.uvScale);
    runwayMat.bumpMap = asphaltTexture;
    runwayMat.bumpScale = cfg.bumpScale;
    // runwayMat.roughnessMap = asphaltTexture;

    // Shared uniform refs — live-updated from SimConfig in update().
    this._runwayUniforms = {
      uAsphaltMap:       { value: asphaltTexture },
      uAsphaltUvScale:   { value: cfg.uvScale },
      uAsphaltBlend:     { value: cfg.blendStrength },
      uAsphaltFadeStart: { value: cfg.fadeStart },
      uAsphaltFadeEnd:   { value: cfg.fadeEnd },
    };

    runwayMat.onBeforeCompile = (shader) => {
      shader.uniforms.uAsphaltMap       = this._runwayUniforms.uAsphaltMap;
      shader.uniforms.uAsphaltUvScale   = this._runwayUniforms.uAsphaltUvScale;
      shader.uniforms.uAsphaltBlend     = this._runwayUniforms.uAsphaltBlend;
      shader.uniforms.uAsphaltFadeStart = this._runwayUniforms.uAsphaltFadeStart;
      shader.uniforms.uAsphaltFadeEnd   = this._runwayUniforms.uAsphaltFadeEnd;

      // ── Vertex: emit a dedicated UV varying so we don't rely on which
      // built-in vUv/vMapUv/vBumpMapUv happens to exist for this material.
      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `#include <common>
varying vec2 vRunwayUv;`
      );
      shader.vertexShader = shader.vertexShader.replace(
        '#include <uv_vertex>',
        `#include <uv_vertex>
vRunwayUv = uv;`
      );

      // ── Fragment: compute a distance-based fade in [0,1] from the
      // fragment-to-camera distance (vViewPosition is emitted by every
      // non-FLAT_SHADED material). The fade scales the colour-multiply
      // strength and the bump perturbation (see normal_fragment_maps
      // replacement below) so high-frequency detail disappears past
      // fadeEnd instead of aliasing or fighting fog at long range.
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `#include <common>
varying vec2 vRunwayUv;
uniform sampler2D uAsphaltMap;
uniform float uAsphaltUvScale;
uniform float uAsphaltBlend;
uniform float uAsphaltFadeStart;
uniform float uAsphaltFadeEnd;
float asphaltFade() {
  return 1.0 - smoothstep(uAsphaltFadeStart, uAsphaltFadeEnd, length(vViewPosition));
}`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
{
  vec3 asphaltSample = texture2D(uAsphaltMap, vRunwayUv * uAsphaltUvScale).rgb;
  diffuseColor.rgb *= mix(vec3(1.0), asphaltSample, uAsphaltBlend * asphaltFade());
}`
      );
      // Bump fade — scale the height gradient by the same fade factor.
      // dHdxy_fwd() returns a vec2, so multiplying by a scalar attenuates
      // the perturbation linearly. At fade = 0 the surface is flat again.
      shader.fragmentShader = shader.fragmentShader.replace(
        'normal = perturbNormalArb( - vViewPosition, normal, dHdxy_fwd(), faceDirection );',
        'normal = perturbNormalArb( - vViewPosition, normal, dHdxy_fwd() * asphaltFade(), faceDirection );'
      );
    };

    runwayMat.needsUpdate = true;
    this._runwayMat = runwayMat;
  }

  update(dt, camera = null) {
    const cfg = AIRFIELD;

    // Live-apply XZ / rotation / scale so SimConfig edits take effect without
    // a full restart.
    this.group.position.x = cfg.position.x;
    this.group.position.z = cfg.position.z;
    this.group.rotation.y = cfg.rotationDeg * DEG2RAD;
    this.group.scale.setScalar(cfg.scale);

    // Live-update runway detail shader params + bump tiling so SimConfig
    // edits hot-reload without a full restart.
    if (this._runwayMat && this._runwayUniforms) {
      const r = cfg.runway;
      this._runwayUniforms.uAsphaltUvScale.value   = r.uvScale;
      this._runwayUniforms.uAsphaltBlend.value     = r.blendStrength;
      this._runwayUniforms.uAsphaltFadeStart.value = r.fadeStart;
      this._runwayUniforms.uAsphaltFadeEnd.value   = r.fadeEnd;
      this._runwayMat.bumpScale = r.bumpScale;
      if (this._runwayMat.bumpMap) {
        this._runwayMat.bumpMap.repeat.set(r.uvScale, r.uvScale);
      }
    }

    const h = this.terrainCollider?.getHeightAt(cfg.position.x, cfg.position.z);
    if (h !== null && h !== undefined && !Number.isNaN(h)) {
      this.group.position.y = h + cfg.yOffset;
      if (!this._placed) {
        this._placed = true;
        this.group.visible = true;
      }
    }

    if (camera && this._detailMeshes.length) {
      const dx = camera.position.x - this.group.position.x;
      const dy = camera.position.y - this.group.position.y;
      const dz = camera.position.z - this.group.position.z;
      const visible = dx * dx + dy * dy + dz * dz < DETAIL_LOD_DISTANCE_SQ;
      for (const m of this._detailMeshes) m.visible = visible;
    }
  }

  dispose() {
    this.scene.remove(this.group);
  }
}
